import React, { useState, useCallback, useEffect, useRef } from "react";
import { CircularProgress, FormGroup, FormControlLabel, Checkbox, Radio, RadioGroup } from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import ReactConfetti from "react-confetti";
import useSound from "use-sound";
import update from "immutability-helper";
import { useDispatch } from "react-redux";
// COMPONENTS
import { AnimatedButton, DesignedBox } from "Components/Commons";
import DraggableList, { Card } from "Components/Commons/DraggableList";
// STYLES
import { DialogWrapper, Input, DraggingOption } from "./Questions.style";
// REDUX
import { showToast } from "Redux/General/Actions";
import { updateTotalScore } from "Redux/Auth/Actions";
// HELPERS
import { PRICES_TYPE, Api, URL_CIVICS_DASHBOARD } from "Helpers";
// SOUND
import ApplauseSound from "Assets/Sounds/Common/Applause.mp3";
import GameOverSound from "Assets/Sounds/Common/GameOver.wav";

const QUESTION_TYPES = {
	TEXT_FIELD: 1,
	SINGLE_CHOICE: 2,
	MULTI_CHOICE: 3,
	RANKING_CHOICE: 4,
};

const PHASES = {
	INIT: "INIT",
	IN_PROGRESS: "",
	CALCULATING_RESULT: "CALCULATING_RESULT",
	FINAL: "FINAL",
	COMPLETED: "COMPLETED",
};

let initQuestion = {
	question: "",
	type: QUESTION_TYPES.SINGLE_CHOICE,
	options: [],
};

function Questions(props) {
	const dispatch = useDispatch();
	let [question, setQuestion] = useState(initQuestion),
		[questions, setQuestions] = useState([]),
		[isLoading, setLoading] = useState(false),
		[phase, setPhase] = useState(PHASES.INIT),
		[answered, setAnswered] = useState([]),
		[result, setResult] = useState({ totalRightAnswer: 0, totalWrongAnswer: 5, totalEarnPoints: 100000 }),
		[escapeRoomResultId, setEscapeRoomResultId] = useState(""),
		[escapeRoomQuestionId, setEscapeRoomQuestionId] = useState(""),
		[escapeRoomCompleted, setEscapeRoomCompleted] = useState(false),
		[gameScore, setGameScore] = useState(0);

	let textRef = useRef(null);

	const navigate = useNavigate();

	const [playApplause, { stop: stopApplauseSound }] = useSound(ApplauseSound, { volume: 0.2 });
	const [playGameOver, { stop: stopGameOverSound }] = useSound(GameOverSound, { volume: 0.3 });

	useEffect(() => {
		return function cleanup() {
			stopApplauseSound();
			stopGameOverSound();
		};
	}, [stopApplauseSound, stopGameOverSound]);

	function changeCheckboxValues(e, value) {
		let newAnswer = [...question.answer];
		if (value) newAnswer.push(parseInt(e.target.name));
		else newAnswer.splice(newAnswer.indexOf(parseInt(e.target.name)), 1);

		setQuestion({
			...question,
			answer: newAnswer,
		});
	}
	function handleRadioChange(e, value) {
		setQuestion({
			...question,
			answer: value,
		});
	}

	function handleTextChange(e) {
		setQuestion({
			...question,
			answer: e.target.value,
		});
	}

	useEffect(() => {
		if (props.open) {
			setPhase(PHASES.INIT);
			getQuestion();
		}
		// eslint-disable-next-line
	}, [props.open]);

	const moveCard = useCallback(
		(dragIndex, hoverIndex) => {
			let dragOption = question.answer[dragIndex],
				answer = [...question.answer];

			let newAnswer = update(answer, {
				$splice: [
					[dragIndex, 1],
					[hoverIndex, 0, dragOption],
				],
			});

			setQuestion({
				...question,
				answer: newAnswer,
			});
		},
		[question]
	);

	async function getQuestion() {
		setLoading(true);

		let response = await new Api().get("executive/escape-room");

		setQuestions(response.data);

		if (!response.data.length) {
			setLoading(false);
			setPhase(PHASES.COMPLETED);
			return;
		}

		let data = response.data[0];

		setEscapeRoomResultId(data.escapeRoomResultId);
		setEscapeRoomQuestionId(data.escapeRoomQuestionId);

		let question = {
			id: data.escapeRoomQuestionId,
			question: data.question,
			options: [],
			type: data.questionType,
			questionFormat: data.questionFormat,
			questionImageUrl: data.questionImageUrl,
			questionVideoUrl: data.questionVideoUrl,
		};

		if (data.questionType === QUESTION_TYPES.SINGLE_CHOICE) {
			question.options = optionObjectToArray(data.singleChoiceTypeQuestion);
			question.answer = "";
		} else if (data.questionType === QUESTION_TYPES.MULTI_CHOICE) {
			question.options = optionObjectToArray(data.multipleChoiceTypeQuestion);
			question.answer = [];
		} else if (data.questionType === QUESTION_TYPES.RANKING_CHOICE) {
			question.answer = [];
			data.rankingTypeQuestion.answers.forEach((option, index) => {
				question.answer.push({
					text: option,
					id: index + 1,
				});
			});
		} else {
			question.answer = data.textTypeQuestion ? data.textTypeQuestion : "";
		}

		setQuestion(question);
		setLoading(false);
		setPhase(PHASES.IN_PROGRESS);

		function optionObjectToArray(object) {
			let options = [];
			for (const key in object) {
				if (key.includes("option")) {
					options.push({
						text: object[key],
						value: key[key.length - 1],
					});
				}
			}

			return options;
		}
	}

	// isCloseButtonClicked=false
	async function submitAnswer() {
		if (phase === PHASES.COMPLETED) {
			// history.push(URL_CIVICS_DASHBOARD);
			navigate(URL_CIVICS_DASHBOARD);
		}

		setLoading(true);

		let payload = {
			escapeRoomQuestionId: escapeRoomQuestionId,
			escapeRoomResultId: escapeRoomResultId,
			multipleChoiceAnswer: [],
			// questionId: question.id,
			questionType: question.type,
			rankingBasedAnswer: [],
			singleChoiceAnswer: 0,
			textTypeAnswer: "",
		};

		let isAnsNull = false;

		switch (question.type) {
			case QUESTION_TYPES.RANKING_CHOICE:
				payload.rankingBasedAnswer = question.answer.map((option) => option.text);
				isAnsNull = payload.rankingBasedAnswer.length > 0 ? false : true;
				break;

			case QUESTION_TYPES.MULTI_CHOICE:
				payload.multipleChoiceAnswer = question.answer.map((item) => parseInt(item));
				isAnsNull = payload.multipleChoiceAnswer.length > 0 ? false : true;
				break;

			case QUESTION_TYPES.SINGLE_CHOICE:
				payload.singleChoiceAnswer = question.answer;
				isAnsNull = payload.singleChoiceAnswer ? false : true;
				break;

			case QUESTION_TYPES.TEXT_FIELD:
				payload.textTypeAnswer = question.answer;
				isAnsNull = payload.textTypeAnswer ? false : true;
				break;

			default:
				break;
		}

		if (isAnsNull) {
			setLoading(false);
			dispatch(showToast("Please enter answer !!"));
			return;
		}

		let newAnswered = [...answered];
		newAnswered.push(payload);

		// && !isCloseButtonClicked
		if (questions.length === newAnswered.length && phase !== PHASES.FINAL) {
			let response = await new Api().put("executive/escape-room", { data: { escapeRoomData: newAnswered, isEscapeRoomComplete: props?.gameStatus?.isEscapeRoomComplete } });

			if (response.data) {
				if (response.data?.isEscapeRoomComplete) {
					setPhase(PHASES.COMPLETED);
					playApplause();
					setEscapeRoomCompleted(true);
					setGameScore(response?.data?.totalGameScore);
				} else {
					setPhase(PHASES.FINAL);
					playGameOver();
				}
			}

			setResult(response.data);
			setLoading(false);
			return;
		}

		let newQuestions = [...questions];
		let index = questions.findIndex((item) => item.escapeRoomQuestionId === question.id);
		setAnswered(newAnswered);
		updateQuestionToNext(newQuestions[index + 1]);
		setLoading(false);

		// to make text field focus every time
		if (textRef?.current) {
			textRef.current.focus();
		}

		// setPhase(PHASES.FINAL)
	}

	const updateQuestionToNext = (data) => {
		setLoading(true);

		if (!data) return onClose();
		setEscapeRoomResultId(data.escapeRoomResultId);
		setEscapeRoomQuestionId(data.escapeRoomQuestionId);

		let question = {
			id: data.escapeRoomQuestionId,
			question: data.question,
			options: [],
			type: data.questionType,
		};

		if (data.questionType === QUESTION_TYPES.SINGLE_CHOICE) {
			question.options = optionObjectToArray(data.singleChoiceTypeQuestion);
			question.answer = "";
		} else if (data.questionType === QUESTION_TYPES.MULTI_CHOICE) {
			question.options = optionObjectToArray(data.multipleChoiceTypeQuestion);
			question.answer = [];
		} else if (data.questionType === QUESTION_TYPES.RANKING_CHOICE) {
			question.answer = [];
			data.rankingTypeQuestion.answers.forEach((option, index) => {
				question.answer.push({
					text: option,
					id: index + 1,
				});
			});
		} else {
			question.answer = data.textTypeQuestion ? data.textTypeQuestion : "";
		}

		setQuestion(question);
		setLoading(false);

		function optionObjectToArray(object) {
			let options = [];
			for (const key in object) {
				if (key.includes("option")) {
					options.push({
						text: object[key],
						value: key[key.length - 1],
					});
				}
			}

			return options;
		}
	};

	const navigateToDashboard = () => navigate(URL_CIVICS_DASHBOARD);

	const onClose = () => {
		if (result.totalScore) {
			dispatch(updateTotalScore(result.totalScore));
		}

		if (escapeRoomCompleted) {
			navigateToDashboard();
		} else {
			props.handleClose();
		}
	};

	return (
		<DialogWrapper onClose={onClose} open={props.open}>
			<ReactConfetti className="confetti" numberOfPieces={500} recycle={false} run={phase === PHASES.COMPLETED && result.totalRightAnswer ? true : false} />

			<DesignedBox className="flex f-v-center">
				{!isLoading && phase === PHASES.IN_PROGRESS && (
					<div className="content">
						<div className="question-wrapper">
							{question.questionFormat === "IMAGE" && (
								<div>
									<img src={question.questionImageUrl} height={"150px"} width="400px" alt="que-img" />
								</div>
							)}

							{question.questionFormat === "VIDEO" && (
								<div>
									<video width="400" height="150" controls>
										<source src={question.questionVideoUrl} />
									</video>
								</div>
							)}
							<div className="question">{question.question}</div>
						</div>

						<div className="options-wrapper">
							<div className="option-hint">
								{/* If you answer correctly your current points will be doubled. */}
								<br />
								{question.type === QUESTION_TYPES.SINGLE_CHOICE && <>Select any one</>}
								{question.type === QUESTION_TYPES.MULTI_CHOICE && <>Select all correct ones</>}
								{question.type === QUESTION_TYPES.RANKING_CHOICE && <>Arrange options into correct order</>}
							</div>
							<FormGroup row>
								{question.type === QUESTION_TYPES.SINGLE_CHOICE && <RadioOptions options={question.options} answer={question.answer} handleChange={handleRadioChange} />}
								{question.type === QUESTION_TYPES.MULTI_CHOICE && <CheckboxOptions options={question.options} answer={question.answer} handleChange={changeCheckboxValues} />}
								{question.type === QUESTION_TYPES.TEXT_FIELD && <InputOptions handleChange={handleTextChange} answer={question.answer} textRef={textRef} />}
								{
									question.type === QUESTION_TYPES.RANKING_CHOICE && <RankingOption data={question.answer} moveCard={moveCard} />
									// <RankingOption data={question.options} moveCard={moveCard}/>
								}
							</FormGroup>
						</div>
					</div>
				)}

				{isLoading && (
					<div className="progress-container full-width flex f-h-center f-v-center">
						<CircularProgress className="progress text-center" />

						{[PHASES.CALCULATING_RESULT, PHASES.INIT].indexOf(phase) !== -1 && (
							<div className="result">
								<p className="text">Hang on a Second</p>
								<p className="sub-text">
									{phase === PHASES.INIT && <> We are preparing your quiz...</>}
									{phase === PHASES.CALCULATING_RESULT && <> We are calculating your score...</>}
								</p>
							</div>
						)}
					</div>
				)}

				{phase === PHASES.FINAL && (
					<div className="wrapped-up-container">
						<span className={`result-text ${result.totalRightAnswer === 0 && result.totalWrongAnswer > 0 ? "lose" : ""}`}>
							{result.totalRightAnswer === 0 && result.totalWrongAnswer > 0 ? "BETTER LUCK NEXT TIME" : "CONGRATULATIONS"}
						</span>
						<span className="title">YOU GOT</span>
						<div className="score-container">
							<div className="score">
								<span className="score-text correct">{result.totalRightAnswer}</span>
								<span className="status-text">CORRECT</span>
							</div>

							<span className="vs-text">VS</span>

							<div className="score">
								<span className="score-text incorrect">{result.totalWrongAnswer}</span>
								<span className="status-text">INCORRECT</span>
							</div>
						</div>
						<span className="text">You must answer all questions correctly to escape from this room.</span>
						<span className="try-again-text">Click on the key again.</span>
					</div>
				)}

				{phase === PHASES.COMPLETED && (
					<div className="result-container flex f-v-center">
						<div className={"title Correct"}>CONGRATULATIONS...</div>

						<div className="sub-title">You have successfully completed all questions.</div>

						{escapeRoomCompleted && <div>{`Your Score : ${gameScore} `}</div>}

						<div
							className="dash text"
							onClick={() => {
								// history.push(URL_CIVICS_DASHBOARD)
								navigate(URL_CIVICS_DASHBOARD);
							}}
						>
							DASHBOARD
						</div>
					</div>
				)}
			</DesignedBox>

			{phase !== PHASES.COMPLETED && (
				<div className="button-wrapper full-width">
					<AnimatedButton
						onClick={phase === PHASES.COMPLETED ? onClose : submitAnswer}
						// onClick={phase === PHASES.FINAL ? onClose : submitAnswer}
						// onClick={() => submitAnswer(phase === PHASES.FINAL ? true : false)}
						disabled={isLoading ? true : false}
					>
						{phase === PHASES.FINAL || phase === PHASES.COMPLETED ? "Close" : "Next"}
					</AnimatedButton>
				</div>
			)}
		</DialogWrapper>
	);
}

function CheckboxOptions(props) {
	return (
		<>
			{props.options.map((option, index) => (
				<FormControlLabel
					key={option.value}
					className="checkbox"
					control={<Checkbox className="icon" onChange={props.handleChange} checked={props.answer.includes(index + 1) ? true : false} name={option.value} />}
					label={option.text}
				/>
			))}
		</>
	);
}

function RadioOptions(props) {
	return (
		<RadioGroup className="full-width radio-group" aria-label="gender" name="gender1" onChange={props.handleChange}>
			{props.options.map((option) => (
				<FormControlLabel
					key={option.value}
					className="radio"
					value={option.value}
					control={<Radio className="icon" checked={parseInt(option.value) === parseInt(props.answer) ? true : false} />}
					label={option.text}
				/>
			))}
		</RadioGroup>
	);
}

function InputOptions(props) {
	return (
		<div className="input-wrapper full-width">
			<Input className="full-width" placeholder="Write your answer" onChange={props.handleChange} value={props.answer} autoFocus inputRef={props.textRef} />
		</div>
	);
}

function RankingOption(props) {
	return (
		<div className="ranking-wrapper full-width">
			<DraggableList moveCard={props.moveCard}>
				{props.data.map((card, index) => (
					<Card key={card.id} index={index} id={card.id} moveCard={props.moveCard}>
						<DraggingOption>{card.text}</DraggingOption>
					</Card>
				))}
			</DraggableList>
		</div>
	);
}

Questions.defaultProps = {
	type: PRICES_TYPE.SPINS,
};

export default Questions;
